import LBox from "@components/LBox";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { createBranchUrl } from "@libs/branch";
import { appendCid, readCid } from "@libs/rudderstack/cid";
import { useBranchStore } from "@utils/branch/store";
import { getAnonymousId, getSessionAttribution } from "@utils/rudderstack";
import { auth } from "@utils/firebase";
import config from "@utils/config";
import branch from "branch-sdk";
import { ViewStyle } from "react-native";

/**
 * Re-appends the session's cid to a Branch-link target. The landing ingest
 * strips ?cid from the address bar, so it must be restored here — journey C:
 * the Branch link embeds this URL, the store install replays it, and the
 * first app open (and the signup after it) still carries the cid.
 */
const withSessionCid = (target: string): string => {
  const { cid } = getSessionAttribution();
  if (cid && !readCid(target)) {
    return appendCid(target, cid);
  }
  return target;
};

/**
 * The in-app route this page maps to, query included (the old pathname-only
 * form silently dropped every query param).
 */
const currentPathWithCid = (): string =>
  withSessionCid(`${window.location.pathname}${window.location.search}`);

/**
 * A hand-off for the install: the signed-in web visitor's Firebase session
 * mints the two-hour, single-use token (core api `/auth-handoff/mint`), the
 * Branch link carries it as `h`, and the first native open signs in before
 * showing the route. Best effort: without it the install lands signed out,
 * as it always did.
 */
const mintInstallHandoff = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    const idToken = await user.getIdToken();
    const response = await fetch(
      `${config.API_URL}/api/rest/auth-handoff/mint`,
      { method: "POST", headers: { Authorization: `Bearer ${idToken}` } },
    );
    if (!response.ok) return null;
    const body = (await response.json()) as { handoffToken?: string };
    return body.handoffToken ?? null;
  } catch {
    return null;
  }
};

const ViewAppButton = ({
  text,
  url,
  style,
}: {
  text?: string;
  url?: string;
  style?: ViewStyle;
}) => {
  return (
    <LPressable
      onPress={async () => {
        const path = currentPathWithCid();
        const [h, lsn_aid] = await Promise.all([
          mintInstallHandoff(),
          // The web session's anonymous id rides along so the native app's
          // stitch joins the install to this web journey (and, through it, to
          // the Live Link visit that started it when this id was adopted).
          getAnonymousId().catch(() => null),
        ]);
        const link = await createBranchUrl({
          data: {
            // An explicit target also gets the session cid — otherwise the
            // access-ticket "Get the App" buttons would install without it.
            url: url ? withSessionCid(url) : path,
            linkType: "direct-link",
            ...(h ? { h } : {}),
            ...(lsn_aid ? { lsn_aid } : {}),
            title: "Lissen",
            description: "Lissen",
            original_branch_link: useBranchStore.getState().branch_link,
            $marketing_title:
              useBranchStore.getState().marketing_title ?? undefined,
            $link_title: useBranchStore.getState().link_title ?? undefined,
          },
          channel: useBranchStore.getState().channel ?? "link",
          feature: useBranchStore.getState().feature ?? "sharing",
          tags: useBranchStore.getState().tags,
          campaign: useBranchStore.getState().campaign ?? undefined,
          createUrl: (data, linkMetadata) =>
            new Promise<string | null>((resolve) => {
              branch.link(
                {
                  data,
                  channel: linkMetadata.channel,
                  feature: linkMetadata.feature,
                  tags: linkMetadata.tags,
                  campaign: linkMetadata.campaign,
                },
                (error, link) => {
                  if (error) {
                    // Branch errors on web due to ad blockers. falling back to using direct url
                    console.error("::createShareLink error", error);
                    console.log(
                      "::createShareLink using direct url as fallback",
                    );
                    // Branch had an error due to ad blockers, so we use the direct url scheme
                    resolve("lsn:/" + path);
                  } else {
                    console.log("::createShareLink success", link);
                    resolve(link);
                  }
                },
              );
            }),
        });
        // On Safari, window.open doesn't work, so we use window.location.assign
        window.location.assign(link as string);
      }}
      sentryEventName="view-app"
    >
      <LBox
        backgroundColor="white"
        borderRadius="l"
        px="s3"
        py="s1_5"
        style={{ marginRight: -10, ...style }}
      >
        <LText color="black" variant="bodyMSemiBold">
          {text ? text : "View in App"}
        </LText>
      </LBox>
    </LPressable>
  );
};

export default ViewAppButton;
